import React, { useContext, useMemo, useState } from 'react'
import '../index.scss'
import { observer } from 'mobx-react'
import WorkResultsCardTable from '@Components/WorkItemV2/WorkResultsCardTable'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import HistoryCompactWorkItem from '@Components/WorkItemV2/History/HistoryCompactWorkItem'
import HistoryExtendedWorkItem from '@Components/WorkItemV2/History/HistoryExtendedWorkItem'
import NeedsAttentionCompactWorkItem from '@Components/WorkItemV2/History/NeedsAttentionCompactWorkItem'
import NeedsAttentionExtendedWorkItem from '@Components/WorkItemV2/History/NeedsAttentionExtendedWorkItem'
import { StoresContext } from '@App/contexts'
import { PaginationBody } from '@Entities/pagination'
import { HistoryWorkQuery } from '@App/entities/search'
import RootStore from '@App/stores/root-store'
import { RegistrationHistoryTabName } from '@App/enum/registration-history-tab-name'
import SearchPanel from '@Components/Search/SearchPanel'
import useRegistrationHistorySearch from '@Scenes/RightsholderScene/RightsholderCatalogRegistrationHistory/RegistrationHistoryTab/hooks/useRegistrationHistorySearch'
import RegistrationHistoryTabStore, {
    defaultBebopPagination,
} from '@App/stores/rightsholder/registrationHistory/registration-history-tab-store'
import {
    RegistrationHistoryFilter,
    RegistrationHistoryFilterCollection,
    registrationHistorySortOptions,
} from '@App/forms/filters/reg-history-filter'
import ModalPortal from '@Components/Popups/ModalPortal'
import Sidebar from '@Components/Sidebars/Sidebar'
import DownloadSidebar from '@Components/Sidebars/DownloadSidebar'
import { SidebarDownloadType } from '@App/enum/sidebar-download-type'
import RegistrationHistoryExportButton from '@Scenes/RightsholderScene/RightsholderCatalogRegistrationHistory/RegistrationHistoryExportButton'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import { getDefaultRegHistoryFilters } from '@App/stores/rightsholder/registrationHistory/registration-history-store'

interface RegistrationHistoryTabProps {
    type: RegistrationHistoryTabName
    registrationHistoryTabStore: RegistrationHistoryTabStore
}

const RegistrationHistoryTab = ({ type, registrationHistoryTabStore }: RegistrationHistoryTabProps) => {
    const { userStore, registrationHistoryStore } = useContext<RootStore>(StoresContext)
    const [sidebarIsOpen, setSidebarIsOpen] = useState(false)

    const useFilters =
        type === RegistrationHistoryTabName.Completed || type === RegistrationHistoryTabName.NeedsAttention

    // registrationHistoryStore (and its filters) is shared across all four tabs; only the Completed
    // and Needs Attention tabs show/use filters at all, same as filterFieldCollections below.
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<RegistrationHistoryFilter>(
        useFilters ? RegistrationHistoryFilterCollection : undefined
    )
    // A link rarely carries every filter (e.g. no sort), so layer it over the defaults rather than replacing them.
    const linkedFilters = useMemo(
        () => (urlFilterValues ? { ...getDefaultRegHistoryFilters(), ...urlFilterValues } : undefined),
        [urlFilterValues]
    )

    // A link's search terms (?title=…) and filters beat whatever the store holds, and must be in it before the
    // form first renders (it picks its rows up then) — hence a state initializer, once per mount, not an effect.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        registrationHistoryStore.criteriaStore.criteriaOptions
    )
    useState(() => {
        if (urlCriteria) {
            registrationHistoryTabStore.resetStore()
            registrationHistoryStore.seedSearchFromUrl(urlCriteria)
        }
        if (linkedFilters) {
            // Parsed from the URL as a loose DeepPartial; every value present is a concrete string
            // the store's own filter definitions produced, so this narrowing is safe.
            registrationHistoryStore.setCurrentFilters(linkedFilters as RegistrationHistoryFilter)
        }
    })

    const { historyQuery, refetchIfKeyUnchanged } = useRegistrationHistorySearch(
        type,
        registrationHistoryTabStore,
        useFilters
    )

    const onChangeLimit = (newLimit: number) => {
        registrationHistoryTabStore.setLimit(newLimit)
    }

    const onChangePage = (newPage: PaginationBody) => {
        registrationHistoryTabStore.setCurrentPage(newPage.page ?? 1)
    }

    const handleSearchWithNewCriteria = (searchCriteria: HistoryWorkQuery, filter: RegistrationHistoryFilter) => {
        syncCriteriaToUrl(searchCriteria)
        registrationHistoryStore.setCurrentFilters(filter)
        registrationHistoryStore.setCurrentQuery(searchCriteria)
        registrationHistoryTabStore.setPagination(defaultBebopPagination)
        refetchIfKeyUnchanged()
    }

    const reset = () => {
        registrationHistoryStore.resetRegHistory()
        refetchIfKeyUnchanged()
    }

    const closeSidebar = () => {
        setSidebarIsOpen(false)
    }

    const hasFilters = [
        registrationHistoryStore.currentFilters.workHistoryStatusFilter,
        registrationHistoryStore.currentFilters.workHistorySourceFilter,
        registrationHistoryStore.currentFilters.workHistoryOverclaimFilter,
    ].some((filter) => filter?.length)

    const isPending = type === RegistrationHistoryTabName.Pending
    const isNeedsAttention = type === RegistrationHistoryTabName.NeedsAttention
    const canNotExport = isPending || isNeedsAttention || registrationHistoryTabStore.works?.count === 0

    const fetchType = isPending || isNeedsAttention ? FetchType.MLC : FetchType.TRANSACTION

    // Use special components for Needs Attention tab
    const compactWorkComponent = isNeedsAttention ? NeedsAttentionCompactWorkItem : HistoryCompactWorkItem
    const extendedWorkComponent = isNeedsAttention ? NeedsAttentionExtendedWorkItem : HistoryExtendedWorkItem

    return (
        <>
            <div className="registration-history-pending-tab">
                {type === RegistrationHistoryTabName.NeedsAttention && (
                    <div className="needs-attention-warning">
                        <p>
                            <strong>
                                The registrations below could not be processed. Please review the details and resubmit.
                            </strong>
                        </p>
                    </div>
                )}
                <section className="showing-search">
                    <div className="showing-search-right">
                        {registrationHistoryStore.criteriaStore && (
                            <SearchPanel
                                searchCriteriaStore={registrationHistoryStore.criteriaStore}
                                canAddCriteria
                                onSearch={handleSearchWithNewCriteria}
                                disabled={historyQuery.isFetching}
                                onReset={() => {
                                    if (useFilters) {
                                        syncFiltersToUrl(undefined)
                                    }
                                    syncCriteriaToUrl(undefined)
                                    reset()
                                }}
                                filterFieldCollections={useFilters ? RegistrationHistoryFilterCollection : undefined}
                                defaultFilterValues={
                                    useFilters ? (linkedFilters ?? registrationHistoryStore.currentFilters) : undefined
                                }
                                onFilterValueChange={useFilters ? syncFiltersToUrl : undefined}
                                sortOptions={useFilters ? registrationHistorySortOptions : undefined}
                                sortFilterKey={useFilters ? 'sort_by' : undefined}
                                disableAutoComplete
                                extraButtons={
                                    canNotExport
                                        ? []
                                        : [
                                              <RegistrationHistoryExportButton
                                                  key="export-button"
                                                  loading={historyQuery.isFetching}
                                                  setSidebarIsOpen={setSidebarIsOpen}
                                                  disabled={hasFilters}
                                              />,
                                          ]
                                }
                            />
                        )}
                    </div>
                </section>
                <WorkResultsCardTable
                    works={registrationHistoryTabStore.works}
                    fetchType={fetchType}
                    paginationInfo={registrationHistoryTabStore.paginationInfo}
                    limit={registrationHistoryTabStore.limit}
                    onChangePage={onChangePage}
                    onChangeLimit={onChangeLimit}
                    compactWorkComponentType={compactWorkComponent}
                    extendedWorkComponentType={extendedWorkComponent}
                    compact={!!userStore.userInfo?.settings?.showDetailedWorkCards}
                    showCompactToggle
                    loading={historyQuery.isFetching}
                />
            </div>
            <ModalPortal isOpen={sidebarIsOpen} onClose={closeSidebar}>
                <Sidebar onClose={closeSidebar}>
                    <DownloadSidebar
                        closeSidebar={closeSidebar}
                        sidebarDownloadType={SidebarDownloadType.RegistrationHistoryExport}
                        registrationHistoryTabName={type}
                    />
                </Sidebar>
            </ModalPortal>
        </>
    )
}

export default observer(RegistrationHistoryTab)
